import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import { Popover, message as antMessage } from "antd";
import { useSelector } from "react-redux";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import {
  CHAT_EMOJI_LIST,
  CHAT_QUILL_FORMATS,
  isEmptyChatHtml,
} from "./utils/chatContent";
import { compressChatImage } from "./utils/compressChatImage";
import "react-quill/dist/quill.snow.css";

function ChatComposer({ value, onChange, onSend, disabled, sending }) {
  const quillRef = useRef(null);
  const onSendRef = useRef(onSend);
  const busyRef = useRef(false);
  const fileInputRef = useRef(null);
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [emojiOpen, setEmojiOpen] = useState(false);

  useEffect(() => {
    onSendRef.current = onSend;
  }, [onSend]);

  useEffect(() => {
    busyRef.current = !!(disabled || sending);
  }, [disabled, sending]);

  const insertImageDataUrl = useCallback((dataUrl) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : Math.max(0, editor.getLength() - 1);
    editor.insertEmbed(index, "image", dataUrl, "user");
    editor.setSelection(index + 1, 0, "user");
  }, []);

  const ingestImageFile = useCallback(
    async (file) => {
      if (!file || busyRef.current) return;
      try {
        const dataUrl = await compressChatImage(file);
        insertImageDataUrl(dataUrl);
      } catch (err) {
        const code = err?.message;
        if (code === "image_too_large") {
          antMessage.error(
            "Fotoja është shumë e madhe. Provoni një foto më të vogël."
          );
        } else if (code === "not_an_image") {
          antMessage.error("Zgjidhni një skedar foto (JPG/PNG/WebP).");
        } else {
          antMessage.error("Nuk u ngarkua fotoja. Provoni përsëri.");
        }
      }
    },
    [insertImageDataUrl]
  );

  const handleImagePick = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      await ingestImageFile(file);
    },
    [ingestImageFile]
  );

  useEffect(() => {
    let root = null;
    let disposed = false;

    const onPaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.kind === "file" && String(item.type || "").startsWith("image/")) {
          event.preventDefault();
          ingestImageFile(item.getAsFile());
          break;
        }
      }
    };

    // ReactQuill mounts the editor after first paint.
    const timer = window.setTimeout(() => {
      if (disposed) return;
      root = quillRef.current?.getEditor?.()?.root || null;
      root?.addEventListener("paste", onPaste);
    }, 0);

    return () => {
      disposed = true;
      window.clearTimeout(timer);
      root?.removeEventListener("paste", onPaste);
    };
  }, [ingestImageFile]);

  // Keep modules stable — recreating them remounts Quill and collapses the editor.
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image() {
            if (busyRef.current) return;
            fileInputRef.current?.click();
          },
        },
      },
      keyboard: {
        bindings: {
          // Enter sends; Shift+Enter keeps Quill newline.
          enterToSend: {
            key: 13,
            shiftKey: false,
            handler() {
              if (!busyRef.current) {
                onSendRef.current?.();
              }
              return false;
            },
          },
        },
      },
    }),
    []
  );

  const insertEmoji = (emoji) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor || busyRef.current) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, emoji, "user");
    editor.setSelection(index + emoji.length, 0, "user");
    setEmojiOpen(false);
  };

  const empty = isEmptyChatHtml(value);

  return (
    <div
      className={`supportChatComposer ${isDarkMode ? "supportChatComposer--dark" : ""}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImagePick}
      />
      <div className="supportChatComposer__editor">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={CHAT_QUILL_FORMATS}
          placeholder="Shkruani përgjigjen… (Enter dërgon, Shift+Enter rresht i ri)"
          readOnly={!!disabled}
        />
      </div>
      <div className="supportChatComposer__actions">
        <Popover
          open={emojiOpen}
          onOpenChange={setEmojiOpen}
          trigger="click"
          placement="topLeft"
          content={
            <div className="supportChatComposer__emojiGrid">
              {CHAT_EMOJI_LIST.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="supportChatComposer__emojiBtn"
                  onClick={() => insertEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          }
        >
          <button
            type="button"
            className="supportChatComposer__toolBtn"
            disabled={!!disabled || !!sending}
          >
            😊
          </button>
        </Popover>
        <MondayButton
          className="mondayButtonGreen"
          hasIcon={false}
          disabled={empty || disabled || sending}
          onClick={() => onSendRef.current?.()}
        >
          Dërgo
        </MondayButton>
      </div>
    </div>
  );
}

export default ChatComposer;
